import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ContainerConfigurable, ContainerEntity, restrictionType} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {
  IconBox,
  IconDroplet,
  IconEdit,
  IconGauge,
  IconMessageCircleStar,
  IconPower, IconTag,
  IconTruck
} from "@tabler/icons-react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {useContainerAction} from "./useContainerAction";
import {ContainerStatusIcon} from "../../icon/ContainerStatusIcon/ContainerStatusIcon";

const hasRejection = (container: ContainerConfigurable): boolean => {
  return container.restrictions?.find((value) => value.type === restrictionType.rejection) !== undefined;
}

const ALL_CONTAINER_ACTIONS = [
  "EDIT",
  "TOGGLE_ACTIVE",
  "TOGGLE_DO_NOT_DISPATCH",
  "TOGGLE_DNU",
  "TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME",
  "DISPATCH",
  "DISPATCH_SPLIT_PICKUP",
  "DISPATCH_SPLIT_DELIVERY",
  "DISPATCH_REQUEST",
  "TOGGLE_SITE_TRANSPORT_VIA_PIPELINE",
  "TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE",
  "TOGGLE_LACT_METER_DNU",
  "TOGGLE_REJECTION",
  "MANAGE_TAGS",
] as const;

export type ContainerAction = typeof ALL_CONTAINER_ACTIONS[number];

export type ContainerActionMenuDropdownProps = {
  container: ContainerConfigurable
  actions?: ContainerAction[]
  onActionClick?: (action: ContainerAction) => void
  showEmpty?: boolean
}

export const ContainerActionMenuDropdown = (props: ContainerActionMenuDropdownProps) => {
  const {
    container,
    actions = ALL_CONTAINER_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess, hasEntityAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<ContainerAction> = useMemo(() => {
    const enabledActions = new Set<ContainerAction>()
    const actionsSet = actions ? new Set<ContainerAction>(actions) : new Set<ContainerAction>()

    let canEditContainer : boolean
    if (container instanceof ContainerEntity) {
      canEditContainer = hasEntityAccess(container, 'container_edit')
    } else {
      canEditContainer = hasAccess({
        resource: {
          id: container.id,
          name: container.name,
          siteId: container.siteId,
          siteName: container.siteName,
          siteCompanyId: container.siteCompanyId,
          siteCompanyName: container.siteCompanyName,
        }
      }, "container_edit")
    }

    if (canEditContainer) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("dispatchrequest_edit")) {
      if (actionsSet.has("DISPATCH_REQUEST")) {
        enabledActions.add("DISPATCH_REQUEST")
      }
    }

    if (hasAccess("dispatch_create")) {
      if (actionsSet.has("DISPATCH")) {
        enabledActions.add("DISPATCH")
      }
    }

    if (hasAccess("dispatchrequest_edit")) {
      if (actionsSet.has("TOGGLE_ACTIVE")) {
        enabledActions.add("TOGGLE_ACTIVE")
      }
      if (actionsSet.has("TOGGLE_DO_NOT_DISPATCH")) {
        enabledActions.add("TOGGLE_DO_NOT_DISPATCH")
      }
      if (actionsSet.has("TOGGLE_DNU")) {
        enabledActions.add("TOGGLE_DNU")
      }
      if (actionsSet.has("TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME")) {
        enabledActions.add("TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME")
      }
      if (actionsSet.has("TOGGLE_SITE_TRANSPORT_VIA_PIPELINE")) {
        if (container.siteId && typeof container.siteTransportViaPipeline !== 'undefined') {
          enabledActions.add("TOGGLE_SITE_TRANSPORT_VIA_PIPELINE")
        }
      }
      if (actionsSet.has("TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE")) {
        if (container.siteId && typeof container.siteTransportOilViaPipeline !== 'undefined') {
          enabledActions.add("TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE")
        }
      }
      if (actionsSet.has("TOGGLE_LACT_METER_DNU")) {
        if (container.lactMeters?.length === 1) {
          enabledActions.add("TOGGLE_LACT_METER_DNU")
        }
      }
      if (actionsSet.has("TOGGLE_REJECTION")) {
        enabledActions.add("TOGGLE_REJECTION")
      }
      if (actionsSet.has("MANAGE_TAGS")) {
        enabledActions.add("MANAGE_TAGS")
      }
    }

    return enabledActions
  }, [container, actions, hasAccess, hasEntityAccess])

  const {
    onContainerActionClick
  } = useContainerAction()

  const _onActionClick = useCallback((action: ContainerAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onContainerActionClick(container, action)
    }
  }, [onActionClick, onContainerActionClick, container])

  const hasTransportViaPipelineAction = useMemo(() => {
    return visibleActions.has("TOGGLE_SITE_TRANSPORT_VIA_PIPELINE") || visibleActions.has("TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE")
  }, [visibleActions])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item
          onClick={() => _onActionClick("EDIT")}
          icon={<IconEdit size={14}/>}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DISPATCH_REQUEST") &&
        <Menu.Item
          icon={<IconMessageCircleStar size={14}/>}
          onClick={() => _onActionClick("DISPATCH_REQUEST")}>
          {t("common.action.dispatchrequest", "Request Dispatch")}
        </Menu.Item>
      }
      {visibleActions.has("DISPATCH") &&
        <Menu.Item onClick={() => _onActionClick("DISPATCH")}>
          {t("common.action.dispatch", "New Dispatch")}
        </Menu.Item>
      }
      {visibleActions.has("TOGGLE_DO_NOT_DISPATCH") &&
        <Menu.Item
          icon={container.doNotDispatch ? <IconTruck size={14}/> :
            <ContainerStatusIcon name="do-not-dispatch" variant="outlined" width={14} height={14}/>}
          onClick={() => _onActionClick("TOGGLE_DO_NOT_DISPATCH")}>
          {container.doNotDispatch ? t("common.action.markdnd.false", "Mark Dispatchable") : t("common.action.markdnd.true", "Mark Do Not Dispatch")}
        </Menu.Item>
      }
      {visibleActions.has("TOGGLE_DNU") &&
        <Menu.Item
          icon={container.doNotUse ? <IconBox size={14}/> :
            <ContainerStatusIcon name="do-not-use" variant="outlined" width={14} height={14}/>}
          onClick={() => _onActionClick("TOGGLE_DNU")}>
          {container.doNotUse ? t("common.action.markdnu.false", "Mark Usable") : t("common.action.markdnu.true", "Mark Not Usable")}
        </Menu.Item>
      }
      {visibleActions.has("TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME") &&
        <Menu.Item
          icon={<ContainerStatusIcon name="ignore-max-non-primary-volume" variant="outlined" width={14} height={14}/>}
          onClick={() => _onActionClick("TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME")}>
          {container.dispatchIgnoreMaxNonPrimaryVolume ? t("common.action.entity.container.dispatchignoremaxnonprimaryvolume.false", "Remove Dispatch Ignore Max Non-Primary Volume") : t("common.action.entity.container.dispatchignoremaxnonprimaryvolume.true", "Mark Dispatch Ignore Max Non-Primary Volume")}
        </Menu.Item>
      }
      {(visibleActions.has("TOGGLE_LACT_METER_DNU") && container.lactMeters?.length === 1) &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            icon={container.lactMeters[0].doNotUse ? <IconGauge size={14}/> :
              <ContainerStatusIcon name="lact-meter-disabled" variant="outlined" width={14} height={14}/>}
            onClick={() => _onActionClick("TOGGLE_LACT_METER_DNU")}>
            {container.lactMeters[0].doNotUse ? t("common.action.entity.container.lactmeter.markdnu.false", "Enable LACT Meter") : t("common.action.entity.container.lactmeter.markdnu.true", "Disable LACT Meter")}
          </Menu.Item>
        </>
      }
      {visibleActions.has("TOGGLE_REJECTION") &&
        <>
          <Menu.Divider/>
          <Menu.Item
            onClick={() => _onActionClick("TOGGLE_REJECTION")}
            icon={hasRejection(container) ? <IconDroplet size={14} color={undefined}/> :
              <ContainerStatusIcon name="rejection-restriction" variant="outlined" width={14} height={14}/>}
          >
            {hasRejection(container) ? t("common.action.markrejection.false", "Clear Rejection") : t("common.action.markrejection.true", "Set Rejection")}
          </Menu.Item>
        </>
      }
      {hasTransportViaPipelineAction &&
        <>
          <Menu.Divider/>
          {visibleActions.has("TOGGLE_SITE_TRANSPORT_VIA_PIPELINE") &&
            <Menu.Item
              onClick={() => _onActionClick("TOGGLE_SITE_TRANSPORT_VIA_PIPELINE")}
              icon={container.siteTransportViaPipeline ? <IconTruck size={14}/> :
                <ContainerStatusIcon name="water-pipeline" variant="outlined" width={14} height={14}/> }>
              {container.siteTransportViaPipeline ? t("common.action.marktransportviapipeline.false", "Mark Transport Water Via Truck") : t("common.action.marktransportviapipeline.true", "Mark Transport Water Via Pipeline")}
            </Menu.Item>
          }
          {visibleActions.has("TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE") &&
            <Menu.Item
              onClick={() => _onActionClick("TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE")}
              icon={container.siteTransportOilViaPipeline ? <IconTruck size={14}/> :
                <ContainerStatusIcon name="oil-pipeline" variant="outlined" width={14} height={14}/> }>
              {container.siteTransportOilViaPipeline ? t("common.action.marktransportoilviapipeline.false", "Mark Transport Oil Via Truck") : t("common.action.marktransportoilviapipeline.true", "Mark Transport Oil Via Pipeline")}
            </Menu.Item>
          }
        </>
      }
      {visibleActions.has("TOGGLE_ACTIVE") &&
        <>
          <Menu.Divider/>
          <Menu.Item
            onClick={() => _onActionClick("TOGGLE_ACTIVE")}
            icon={<IconPower size={14} color={container.active ? "red" : undefined}/>}
          >
            {container.active ? t("common.action.markactive.false", "Deactivate") : t("common.action.markactive.true", "Activate")}
          </Menu.Item>
        </>
      }
      {visibleActions.has("MANAGE_TAGS") &&
        <>
          <Menu.Divider/>
          <Menu.Item
            onClick={() => _onActionClick("MANAGE_TAGS")}
            icon={<IconTag size={14}/>}
          >
            {t("common.action.managetags", "Manage Tags")}
          </Menu.Item>
        </>
      }
    </>
  )
}
